import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { getCustomerCompanies } from "../../../../services/staffingService";
import DashboardSidebar from "../agencyDashboardSidebar";
import {
  Building2, Search, Plus, ChevronLeft, ChevronRight,
  Phone, Mail, DollarSign, Globe, ChevronRight as Arrow,
} from "lucide-react";
import { motion } from "framer-motion";

const ITEMS_PER_PAGE = 20;

const facilityBadge = (type) => {
  if (!type) return null;
  const colors = {
    Hospital: "bg-blue-50 text-blue-700 border-blue-200",
    Clinic: "bg-violet-50 text-violet-700 border-violet-200",
    "Assisted Living": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Nursing Home": "bg-amber-50 text-amber-700 border-amber-200",
  };
  const cls = colors[type] || "bg-muted text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}>
      {type}
    </span>
  );
};

export default function CustomerList() {
  const { providerCompanyId } = useParams();
  const history = useHistory();

  const [showSidebar, setShowSidebar]         = useState(false);
  const [companies, setCompanies]             = useState([]);
  const [page, setPage]                       = useState(0);
  const [loading, setLoading]                 = useState(true);
  const [search, setSearch]                   = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(value);
    }, 350);
  }, []);

  const fetchCompanies = useCallback(async () => {
    if (!providerCompanyId) return;
    setLoading(true);
    try {
      const resp = await getCustomerCompanies(providerCompanyId);
      setCompanies(resp.data.customerCompanies || []);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  // Client-side filter + paginate
  const filtered = companies.filter((c) => {
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      (c.companyFriendlyName || "").toLowerCase().includes(q) ||
      (c.companyLegalName || "").toLowerCase().includes(q) ||
      (c.contact?.contactPersonName || "").toLowerCase().includes(q) ||
      (c.contact?.email || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageItems  = filtered.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Customers</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {loading ? "Loading…" : `${filtered.length} customer${filtered.length !== 1 ? "s" : ""} in your network`}
              </p>
            </div>
            <button
              onClick={() => history.push(`/provider-company/${providerCompanyId}/crm/new`)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary/90"
            >
              <Plus size={16} />
              New Customer
            </button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by name, contact, email…"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-pink"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {["Customer", "Facility Type", "Contact", "Billing Rate", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                        No customers found.
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((company, index) => (
                      <motion.tr
                        key={company._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="cursor-pointer transition-colors hover:bg-muted/30"
                        onClick={() =>
                          history.push(`/provider-company/${providerCompanyId}/crm/${company._id}`)
                        }
                      >
                        {/* Customer */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                              <Building2 size={16} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-card-foreground">
                                {company.companyFriendlyName || company.companyLegalName}
                              </p>
                              {company.companyFriendlyName !== company.companyLegalName && company.companyLegalName && (
                                <p className="truncate text-xs text-muted-foreground">{company.companyLegalName}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Facility Type */}
                        <td className="px-4 py-3">
                          {facilityBadge(company.facilityType) || (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Contact */}
                        <td className="px-4 py-3">
                          <div className="space-y-0.5">
                            {company.contact?.contactPersonName && (
                              <p className="text-sm font-medium text-card-foreground">
                                {company.contact.contactPersonName}
                              </p>
                            )}
                            {company.contact?.phone && (
                              <div className="flex items-center gap-1.5">
                                <Phone size={11} className="text-muted-foreground" />
                                <span className="text-xs text-card-foreground">{company.contact.phone}</span>
                              </div>
                            )}
                            {company.contact?.email && (
                              <div className="flex items-center gap-1.5">
                                <Mail size={11} className="text-muted-foreground" />
                                <span className="max-w-[160px] truncate text-xs text-card-foreground">
                                  {company.contact.email}
                                </span>
                              </div>
                            )}
                            {!company.contact?.contactPersonName && !company.contact?.phone && !company.contact?.email && (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </div>
                        </td>

                        {/* Billing Rate */}
                        <td className="px-4 py-3">
                          {company.preferences?.hourlyBillingRate ? (
                            <div className="flex items-center gap-1.5">
                              <DollarSign size={13} className="text-emerald-600" />
                              <span className="text-sm font-medium text-card-foreground">
                                ${company.preferences.hourlyBillingRate}/hr
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Arrow */}
                        <td className="px-2 py-3">
                          <Arrow size={14} className="text-muted-foreground" />
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {page * ITEMS_PER_PAGE + 1}–
                {Math.min((page + 1) * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40"
                >
                  <ChevronLeft size={14} /> Back
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
